/**
 * Period coherence for COMBINED numbers.
 *
 * THE PROBLEM
 * -----------
 * `evidence-as-of.ts` removed age as an admission test and left this note:
 *
 *   "Age is not the risk. Period incoherence is: summing FY2025 finance with
 *    FY2023 carrier volumes is fiction no matter how fresh either input is.
 *    That check belongs where numbers COMBINE."
 *
 * This is that check. Two approved artifacts from different fiscal periods can
 * be summed today, both carrying honest as-of dates, and the sum foots
 * arithmetically while meaning nothing. Nothing in the stack tested for it —
 * not the freshness gates that were removed, and not the ones that remain. It
 * is an exposed gap, not a regression.
 *
 * THE RULE
 * --------
 *  1. It applies ONLY when numbers combine — a sum, a bridge, a reconciliation,
 *     a comparison. A single readout carrying one as-of needs nothing from this
 *     module, and `assessPeriodCoherence` returns `coherent` for fewer than two
 *     inputs precisely so callers cannot accidentally label a lone figure.
 *
 *  2. COMPARABLE IS NOT IDENTICAL. The comparison key is the fiscal YEAR, not
 *     the as-of instant. Two artifacts from FY2025 read on different days both
 *     describe FY2025 and combine honestly — that is a normal reporting-cut
 *     difference, not incoherence. FY2025 against FY2023 is a different claim
 *     about the world and gets labelled. Within a year, combining a Q1 figure
 *     with a Q3 figure is ordinary arithmetic (year-to-date, quarter-over-
 *     quarter), so sub-year differences are deliberately NOT flagged.
 *
 *  3. On mismatch we LABEL, naming both periods. We do not block and we do not
 *     silently sum. Only security stops an answer; this is not security.
 *
 *  4. When a combining input carries no period metadata at all, the verdict is
 *     `unknown_period` and the disclosure says which input was undated. It is
 *     never `coherent`. Asserting coherence we cannot evidence is the failure
 *     mode this module exists to prevent — silence is worse than a label.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO
 * ----------------------------------
 *  - It does not check GRAIN. Summing an FY2025 annual total with an FY2025
 *    quarterly figure double-counts, and both resolve to `FY2025`, so this
 *    module returns `coherent`. That is a different defect and claiming
 *    otherwise here would be a false assurance.
 *  - It does not read a tenant fiscal calendar, because none is carried on the
 *    binding contract. A fiscal year that does not end in December, resolved
 *    from a bare as-of, can land in the adjacent calendar year and read as
 *    spanning. That errs toward a DISCLOSURE — the honest direction. It can
 *    never withhold a number or change one. Callers that know their fiscal
 *    calendar should put an explicit period on `timeframe.label` (`FY2025`,
 *    `Q3 FY2025`) or a `start`/`end` interval, both of which win over the
 *    as-of fallback.
 *  - It does not decide anything. Every function here returns a description.
 */

/** How a period was determined, most authoritative first. */
export type PeriodBasis =
  /** An explicit fiscal token on the declared label, e.g. "FY2025". */
  | 'declared_label'
  /** A declared start/end interval. */
  | 'declared_interval'
  /** Fallback: the calendar year of the artifact's as-of. */
  | 'as_of'
  /** Nothing on the input names a period. */
  | 'undated';

export type ResolvedPeriod = {
  /**
   * Canonical comparison key, e.g. `"FY2025"`. `null` when the input carries no
   * period metadata — which is a reportable state, never a silent pass.
   */
  key: string | null;
  basis: PeriodBasis;
  /** Reader-facing period name. Same as `key` when known. */
  label: string | null;
};

export type PeriodCoherenceVerdict =
  /** Every input resolves to the same period. */
  | 'coherent'
  /** Inputs resolve to two or more different periods. */
  | 'spans_periods'
  /** At least one input carries no period, so coherence cannot be confirmed. */
  | 'unknown_period';

/** One number entering a combination. */
export type PeriodCoherenceInput = {
  /** Reader-facing name of the figure, e.g. "Carrier spend". */
  label: string;
  /** The binding timeframe, when the caller has one. */
  timeframe?: {
    label?: unknown;
    asOf?: unknown;
    start?: unknown;
    end?: unknown;
  } | null;
  /**
   * Fallback as-of used only when the timeframe names no period — typically
   * `binding.source.asOf` or an evidence ref's `asOf`.
   */
  asOf?: unknown;
};

export type PeriodCoherenceInputAssessment = {
  label: string;
  period: ResolvedPeriod;
};

export type PeriodCoherenceDisclosure = {
  verdict: PeriodCoherenceVerdict;
  /** Distinct resolved periods, ascending. Empty when nothing resolved. */
  periods: string[];
  /** Labels of the inputs that carry no period at all. */
  undatedInputs: string[];
  /**
   * The sentence a reader sees. `null` when the verdict is `coherent` — a
   * coherent combination needs no extra sentence; the figures carry their own
   * as-ofs already.
   */
  label: string | null;
  /** Per-input resolution, for diagnostics and proof artifacts. */
  inputs: PeriodCoherenceInputAssessment[];
};

/**
 * `FY2025`, `FY 2025`, `FY25`, `Q3 FY2025`, `FY2025 Q3`, `fiscal year 2025`,
 * and a bare four-digit year inside a longer label ("2025 operating plan").
 * A bare two-digit number is NOT accepted — too ambiguous to key on.
 */
const FISCAL_YEAR_PATTERNS: readonly RegExp[] = [
  /\bfy\s*(\d{4})\b/i,
  /\bfy\s*(\d{2})\b/i,
  /\bfiscal\s+year\s+(\d{4})\b/i,
  /\b(19|20)(\d{2})\b/,
];

const MIN_PLAUSIBLE_YEAR = 1900;
const MAX_PLAUSIBLE_YEAR = 2999;

/**
 * Resolve one input's period.
 *
 * Order is authority order: an explicitly declared period beats a declared
 * interval, which beats the as-of fallback. The `basis` travels on the result
 * so a reader (or a proof artifact) can see how confident the resolution is.
 */
export function resolvePeriod(input: PeriodCoherenceInput): ResolvedPeriod {
  const declared = fiscalYearFromLabel(input.timeframe?.label);
  if (declared !== null) return periodFor(declared, 'declared_label');

  const interval = fiscalYearFromInterval(
    input.timeframe?.start,
    input.timeframe?.end,
  );
  if (interval !== null) return periodFor(interval, 'declared_interval');

  const asOfYear =
    calendarYearOf(input.timeframe?.asOf) ?? calendarYearOf(input.asOf);
  if (asOfYear !== null) return periodFor(asOfYear, 'as_of');

  return { key: null, basis: 'undated', label: null };
}

/**
 * Assess a set of numbers that are about to be combined.
 *
 * Returns a description, never a verdict that can stop anything. Fewer than two
 * inputs is always `coherent`: one number combines with nothing.
 */
export function assessPeriodCoherence(
  inputs: readonly PeriodCoherenceInput[],
): PeriodCoherenceDisclosure {
  const assessments: PeriodCoherenceInputAssessment[] = inputs.map((input) => ({
    label: normalizeLabel(input.label) ?? 'Unnamed figure',
    period: resolvePeriod(input),
  }));

  const periods = [
    ...new Set(
      assessments
        .map((assessment) => assessment.period.key)
        .filter((key): key is string => key !== null),
    ),
  ].sort();
  const undatedInputs = assessments
    .filter((assessment) => assessment.period.key === null)
    .map((assessment) => assessment.label);

  // A single readout combines with nothing. Say nothing about it.
  if (assessments.length < 2) {
    return {
      verdict: 'coherent',
      periods,
      undatedInputs: [],
      label: null,
      inputs: assessments,
    };
  }

  // Spanning outranks unknown: two named, different periods is the concrete
  // claim of fiction, and the sentence still names any undated input as well.
  if (periods.length > 1) {
    return {
      verdict: 'spans_periods',
      periods,
      undatedInputs,
      label: buildSpanningSentence(periods, undatedInputs),
      inputs: assessments,
    };
  }

  if (undatedInputs.length > 0) {
    return {
      verdict: 'unknown_period',
      periods,
      undatedInputs,
      label: buildUnknownPeriodSentence(periods, undatedInputs),
      inputs: assessments,
    };
  }

  return {
    verdict: 'coherent',
    periods,
    undatedInputs: [],
    label: null,
    inputs: assessments,
  };
}

/** True when the combination needs a period sentence on the surface. */
export function periodCoherenceNeedsDisclosure(
  disclosure: PeriodCoherenceDisclosure,
): boolean {
  return disclosure.verdict !== 'coherent';
}

/**
 * The reader-facing sentence for a combination that spans periods.
 *
 * It states which periods were combined and stops. No "error", no "warning", no
 * apology — the figures are each real and each approved; what the reader needs
 * is the fact that they describe different years.
 */
export function buildSpanningSentence(
  periods: readonly string[],
  undatedInputs: readonly string[] = [],
): string {
  const spanning = `This combines figures from ${joinWithAnd(periods)}.`;
  if (undatedInputs.length === 0) return spanning;
  return `${spanning} ${buildUndatedClause(undatedInputs)}`;
}

/**
 * The sentence for a combination we cannot confirm. Naming the undated input is
 * the whole point: "we don't know" is only useful when it says what we don't
 * know about.
 */
export function buildUnknownPeriodSentence(
  periods: readonly string[],
  undatedInputs: readonly string[],
): string {
  const known =
    periods.length === 0
      ? 'None of these figures states the period it covers.'
      : `These figures combine with ${joinWithAnd(periods)}.`;
  return `${known} ${buildUndatedClause(undatedInputs)}`;
}

function buildUndatedClause(undatedInputs: readonly string[]): string {
  const named = joinWithAnd(undatedInputs);
  return undatedInputs.length === 1
    ? `${named} does not state the period it covers, so this combination cannot be confirmed to cover one period.`
    : `${named} do not state the period they cover, so this combination cannot be confirmed to cover one period.`;
}

function periodFor(year: number, basis: PeriodBasis): ResolvedPeriod {
  const key = `FY${year}`;
  return { key, basis, label: key };
}

function fiscalYearFromLabel(value: unknown): number | null {
  const text = normalizeLabel(value);
  if (text === null) return null;
  for (const pattern of FISCAL_YEAR_PATTERNS) {
    const match = pattern.exec(text);
    if (!match) continue;
    // `\b(19|20)(\d{2})\b` splits the year across two groups; the others put the
    // whole year in group 1.
    const digits =
      match.length > 2 && match[2] !== undefined
        ? `${match[1]}${match[2]}`
        : match[1];
    const year = expandYear(digits);
    if (year !== null) return year;
  }
  return null;
}

/**
 * A declared interval names its period by the year it ENDS in — the ordinary
 * convention for a fiscal year that straddles the calendar boundary (a period
 * running Jul 2024 to Jun 2025 is FY2025). An interval with only one end
 * populated is resolved from whichever end is present.
 */
function fiscalYearFromInterval(start: unknown, end: unknown): number | null {
  return calendarYearOf(end) ?? calendarYearOf(start);
}

function calendarYearOf(value: unknown): number | null {
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value.getUTCFullYear() : null;
  }
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!text) return null;
  const parsed = new Date(text);
  const time = parsed.getTime();
  if (!Number.isFinite(time)) return null;
  const year = parsed.getUTCFullYear();
  return isPlausibleYear(year) ? year : null;
}

function expandYear(digits: string | undefined): number | null {
  if (!digits) return null;
  const numeric = Number.parseInt(digits, 10);
  if (!Number.isFinite(numeric)) return null;
  // "FY25" -> 2025. Two-digit fiscal shorthand is only ever read as 20xx; there
  // is no governed 19xx evidence and guessing a century would be fabrication.
  const year = digits.length === 2 ? 2000 + numeric : numeric;
  return isPlausibleYear(year) ? year : null;
}

function isPlausibleYear(year: number): boolean {
  return (
    Number.isInteger(year) &&
    year >= MIN_PLAUSIBLE_YEAR &&
    year <= MAX_PLAUSIBLE_YEAR
  );
}

function normalizeLabel(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function joinWithAnd(values: readonly string[]): string {
  if (values.length === 0) return '';
  if (values.length === 1) return values[0];
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(', ')} and ${values[values.length - 1]}`;
}
